import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Image,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconShieldCheckered, IconShieldLock } from '@tabler/icons-react';
import {
  confirmMfa,
  disableMfaRequest,
  enrollMfa,
  regenerateRecoveryCodes,
} from '../../auth/auth-api';
import { useAuthStore } from '../../auth/auth-store';
import { fetchMe } from '../../auth/auth-api';
import { ApiError } from '../../lib/api-error';

interface EnrollState {
  qrDataUrl: string;
  otpauthUrl: string;
}

/**
 * MFA management panel for the profile page. Three states:
 *   - already enabled: show "MFA is on" + a Disable button
 *   - mid-enrollment (secret staged): show QR + confirm-code input
 *   - clean: show "Set up MFA" CTA
 *
 * On confirm, the API returns 10 plaintext recovery codes — shown ONCE in a
 * modal with a "I've saved these" affordance before they disappear.
 */
export function MfaPanel() {
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const accessToken = useAuthStore((s) => s.accessToken);
  const [enroll, setEnroll] = useState<EnrollState | null>(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;

  async function refreshUser() {
    try {
      const next = await fetchMe();
      if (accessToken) setSession(next, accessToken);
    } catch {
      // non-fatal; the next page load will pick up the new state
    }
  }

  async function startEnroll() {
    setSubmitting(true);
    try {
      const r = await enrollMfa();
      setEnroll({ qrDataUrl: r.qr_data_url, otpauthUrl: r.otpauth_url });
      setConfirmCode('');
    } catch (err) {
      notifications.show({
        title: 'Could not start MFA enrollment',
        message: err instanceof Error ? err.message : 'unknown',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function submitConfirm() {
    setSubmitting(true);
    try {
      const r = await confirmMfa(confirmCode.trim());
      setEnroll(null);
      setConfirmCode('');
      showRecoveryCodes(r.recovery_codes);
      await refreshUser();
    } catch (err) {
      notifications.show({
        title: 'Verification failed',
        message: err instanceof ApiError ? err.message : 'unknown',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }

  function showRecoveryCodes(codes: string[]) {
    modals.open({
      title: (
        <Group gap="xs">
          <IconShieldCheckered size={18} />
          <span>Save your recovery codes</span>
        </Group>
      ),
      withCloseButton: false,
      closeOnEscape: false,
      closeOnClickOutside: false,
      children: <RecoveryCodesPanel codes={codes} />,
      size: 'md',
    });
  }

  function openDisable() {
    if (user!.mfa_required) {
      notifications.show({
        title: 'Cannot disable',
        message: 'MFA is required for your role.',
        color: 'orange',
      });
      return;
    }
    let password = '';
    let code = '';
    modals.openConfirmModal({
      title: 'Disable MFA?',
      children: (
        <Stack gap="sm">
          <Text size="sm">
            You'll need to re-enter your password and a current TOTP/recovery code. All recovery
            codes will be invalidated.
          </Text>
          <PasswordInput
            label="Current password"
            onChange={(e) => {
              password = e.currentTarget.value;
            }}
          />
          <TextInput
            label="Authenticator code or recovery code"
            onChange={(e) => {
              code = e.currentTarget.value;
            }}
          />
        </Stack>
      ),
      labels: { confirm: 'Disable MFA', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await disableMfaRequest(password, code.trim());
          notifications.show({ title: 'MFA disabled', message: '', color: 'pine' });
          await refreshUser();
        } catch (err) {
          notifications.show({
            title: 'Could not disable MFA',
            message: err instanceof ApiError ? err.message : 'unknown',
            color: 'red',
          });
        }
      },
    });
  }

  function openRegenerate() {
    let password = '';
    let code = '';
    modals.openConfirmModal({
      title: 'Regenerate recovery codes?',
      children: (
        <Stack gap="sm">
          <Text size="sm">
            All unused recovery codes will be replaced with a fresh set. The new codes are shown
            once.
          </Text>
          <PasswordInput
            label="Current password"
            onChange={(e) => {
              password = e.currentTarget.value;
            }}
          />
          <TextInput
            label="Authenticator code or recovery code"
            onChange={(e) => {
              code = e.currentTarget.value;
            }}
          />
        </Stack>
      ),
      labels: { confirm: 'Regenerate', cancel: 'Cancel' },
      onConfirm: async () => {
        try {
          const r = await regenerateRecoveryCodes(password, code.trim());
          showRecoveryCodes(r.recovery_codes);
        } catch (err) {
          notifications.show({
            title: 'Could not regenerate',
            message: err instanceof ApiError ? err.message : 'unknown',
            color: 'red',
          });
        }
      },
    });
  }

  return (
    <Card withBorder padding="lg" radius="md">
      <Group gap="sm" mb="md">
        <IconShieldLock size={20} />
        <Title order={4}>Two-factor authentication</Title>
        {user.mfa_enabled && (
          <Badge color="teal" variant="light">
            Enabled
          </Badge>
        )}
        {user.mfa_required && !user.mfa_enabled && (
          <Badge color="orange" variant="light">
            Required
          </Badge>
        )}
      </Group>

      {user.mfa_required && !user.mfa_enabled && user.mfa_grace_until && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="orange"
          variant="light"
          mb="sm"
          title="MFA enrollment grace period"
        >
          Your account requires MFA, but you have a temporary grace period until{' '}
          {new Date(user.mfa_grace_until).toLocaleString()}. Set up MFA before then to avoid being
          locked out.
        </Alert>
      )}

      {user.mfa_enabled ? (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            MFA is on. You'll be asked for a TOTP code each time you sign in.
          </Text>
          <Group gap="sm">
            <Button variant="light" onClick={openRegenerate}>
              Regenerate recovery codes
            </Button>
            {!user.mfa_required && (
              <Button color="red" variant="subtle" onClick={openDisable}>
                Disable MFA
              </Button>
            )}
          </Group>
        </Stack>
      ) : enroll ? (
        <Stack gap="sm">
          <Text size="sm">
            Scan the QR code with your authenticator app (1Password, Authy, Google Authenticator,
            etc.), then enter the 6-digit code below.
          </Text>
          <Group justify="center" my="sm">
            <Image src={enroll.qrDataUrl} alt="MFA QR code" w={200} h={200} />
          </Group>
          <Text size="xs" c="dimmed" ta="center">
            Manual setup key (if you can't scan): <Code>{enroll.otpauthUrl}</Code>
          </Text>
          <TextInput
            label="6-digit code from authenticator app"
            value={confirmCode}
            onChange={(e) => setConfirmCode(e.currentTarget.value)}
            inputMode="numeric"
            autoFocus
          />
          <Group justify="space-between">
            <Button variant="subtle" onClick={() => setEnroll(null)}>
              Cancel
            </Button>
            <Button onClick={submitConfirm} loading={submitting} disabled={!confirmCode}>
              Verify and enable
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Add an extra layer of security: you'll need a TOTP code from your authenticator app at
            sign-in.
          </Text>
          <Group>
            <Button onClick={startEnroll} loading={submitting}>
              Set up MFA
            </Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}

function RecoveryCodesPanel({ codes }: { codes: string[] }) {
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <Stack gap="sm">
      <Alert color="orange" variant="light">
        These codes are shown <strong>once</strong>. Save them somewhere safe (password manager).
        Each one is single-use.
      </Alert>
      <Card withBorder padding="md">
        <Stack gap={6}>
          {codes.map((c) => (
            <Code key={c} block>
              {c}
            </Code>
          ))}
        </Stack>
      </Card>
      <Group justify="space-between">
        <Button
          variant="light"
          onClick={() => {
            void navigator.clipboard.writeText(codes.join('\n'));
            notifications.show({ title: 'Copied to clipboard', message: '', color: 'pine' });
          }}
        >
          Copy all
        </Button>
        <Button
          color={acknowledged ? 'pine' : 'gray'}
          onClick={() => {
            if (!acknowledged) {
              setAcknowledged(true);
              return;
            }
            modals.closeAll();
          }}
        >
          {acknowledged ? "Yes — I've saved them" : "I've saved these"}
        </Button>
      </Group>
    </Stack>
  );
}
